import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Carregando, Erro } from '../componentes/ui'
import { Kpi, LinhaDeKpis } from '../componentes/grafico/Kpi'
import { ColunasEmpilhadas } from '../componentes/grafico/ColunasEmpilhadas'
import { BarrasRanking } from '../componentes/grafico/BarrasRanking'
import { Funil } from '../componentes/grafico/Funil'
import { brl, brlCompacto, inteiro, mesCurto, pct } from '../lib/formato'
import './relatorios.css'

/**
 * RELATÓRIOS — os números que decidem se a clínica renova a assinatura.
 *
 * ─── AS TRÊS PERGUNTAS ────────────────────────────────────────────
 *
 *  1. "Faturei mais que mês passado?"      → colunas empilhadas
 *  2. "Quem produz, e quanto?"             → ranking com meta
 *  3. "Onde perco horário?"                → funil de comparecimento
 *
 * O motor das três já estava pronto e testado no banco. Faltava a tela.
 *
 * ─── A COR AQUI É DE FOCO, NÃO DE SÉRIE ───────────────────────────
 *
 * A referência que decidiu isto: doze barras, onze cinzas, UMA colorida
 * — a em foco. É a nossa regra da agenda acromática aplicada a gráfico,
 * e é por isso que não existe paleta categórica de cinco cores aqui.
 */

type Produtividade = {
  usuario_id: string; usuario: string
  marcacoes: string; concretizadas: string; faltas: string; cancelamentos: string
  valor_centavos: string; aproveitamento_pct: string | null
}

type Desempenho = {
  profissional_id: string; profissional: string; cor: string | null
  atendimentos: string; bruto_centavos: string
  comissao_centavos: string; comissao_liquidada_centavos: string
}

type Meta = {
  id: string
  alvo_tipo: 'PROFISSIONAL' | 'USUARIO' | 'CLINICA'
  alvo_id: string | null
  indicador: string
  alvo_valor: string
  realizado: string | null
}

type Resumo = {
  recebido_centavos: string; liquidado_centavos: string
  aberto_centavos: string; abertos: string
  vencido_centavos: string; vencidos: string
}

const n = (v: string | null | undefined) => Number(v ?? 0) || 0

/** `2026-08` — o que o `<input type="month">` produz. */
const mesAtual = () => new Date().toISOString().slice(0, 7)

/** Os seis meses até a competência escolhida, do mais antigo ao mais novo. */
function ultimosSeis(competencia: string): string[] {
  const [a, m] = competencia.split('-').map(Number)
  const fim = new Date(a!, (m ?? 1) - 1, 1)
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(fim); d.setMonth(d.getMonth() - (5 - i))
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })
}

export function Relatorios() {
  const [comp, setComp] = useState(mesAtual)
  const [prod, setProd] = useState<Produtividade[]>([])
  const [desemp, setDesemp] = useState<Desempenho[]>([])
  const [metas, setMetas] = useState<Meta[]>([])
  const [serie, setSerie] = useState<Array<{ mes: string; resumo: Resumo }>>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    const nada = () => null
    try {
      const meses = ultimosSeis(comp)
      const [p, d, m, ...resumos] = await Promise.all([
        api<{ itens: Produtividade[] }>(`/v1/painel/produtividade?competencia=${comp}`).catch(nada),
        api<{ itens: Desempenho[] }>(`/v1/painel/desempenho?competencia=${comp}`).catch(nada),
        api<{ itens: Meta[] }>(`/v1/metas?competencia=${comp}`).catch(nada),
        /* Seis chamadas de resumo, uma por mês. Existe um `/v1/relatorios`
           que faria isso numa só — e não existe motivo para criá-lo antes
           de medir: são seis requisições paralelas de leitura, e a rota
           já é usada pelo Financeiro todo dia. */
        ...meses.map((mes) => {
          const [aa, mm] = mes.split('-').map(Number)
          const de = `${mes}-01`
          const ate = new Date(aa!, mm!, 0).toISOString().slice(0, 10)
          return api<Resumo>(`/v1/financeiro/resumo?de=${de}&ate=${ate}`).catch(nada)
        }),
      ])
      setProd(p?.itens ?? []); setDesemp(d?.itens ?? []); setMetas(m?.itens ?? [])
      setSerie(meses.map((mes, i) => ({ mes, resumo: (resumos[i] as Resumo | null) ?? {
        recebido_centavos: '0', liquidado_centavos: '0',
        aberto_centavos: '0', abertos: '0', vencido_centavos: '0', vencidos: '0',
      } })))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar os relatórios.')
    } finally { setCarregando(false) }
  }, [comp])

  useEffect(() => { void carregar() }, [carregar])

  /* ── o funil de comparecimento ─────────────────────────────────── */
  const funil = useMemo(() => {
    const marcadas = prod.reduce((s, x) => s + n(x.marcacoes), 0)
    const faltas = prod.reduce((s, x) => s + n(x.faltas), 0)
    const cancel = prod.reduce((s, x) => s + n(x.cancelamentos), 0)
    const feitas = prod.reduce((s, x) => s + n(x.concretizadas), 0)
    return { marcadas, faltas, cancel, feitas }
  }, [prod])

  const doMes = serie[serie.length - 1]?.resumo
  const anterior = serie[serie.length - 2]?.resumo

  const delta = doMes && anterior && n(anterior.recebido_centavos) > 0
    ? ((n(doMes.recebido_centavos) - n(anterior.recebido_centavos)) / n(anterior.recebido_centavos)) * 100
    : null

  if (carregando) return <Carregando altura={320} />

  const semDado = prod.length === 0 && desemp.length === 0 && funil.marcadas === 0

  return (
    <div className="rel">
      <Erro mensagem={erro} />

      <header className="rel__cab">
        <div>
          <h1>Relatórios</h1>
          <p className="ajuda">O que aconteceu, em número. Recebido é o que o paciente pagou.</p>
        </div>
        <input className="entrada rel__mes" type="month" value={comp}
               aria-label="Competência"
               onChange={(e) => e.target.value && setComp(e.target.value)} />
      </header>

      {semDado ? (
        <div className="cartao rel__vazio">
          <h2>Ainda não há o que medir.</h2>
          <p className="ajuda">
            Os números aparecem sozinhos conforme a clínica trabalha: cada
            atendimento concluído entra na produção, cada recebimento entra no
            faturamento. Não há nada para configurar aqui.
          </p>
        </div>
      ) : (
        <>
          <LinhaDeKpis>
            <Kpi rotulo="Recebido no mês" valor={brl(doMes?.recebido_centavos)}
                 ajuda="o que o paciente pagou"
                 delta={delta} periodo="vs. mês anterior" />
            <Kpi rotulo="Caiu na conta" valor={brl(doMes?.liquidado_centavos)}
                 ajuda="dinheiro disponível de fato" />
            <Kpi rotulo="Em aberto" valor={brl(doMes?.aberto_centavos)}
                 ajuda={`${inteiro(doMes?.abertos)} lançamento(s)`} />
            <Kpi rotulo="Vencido" valor={brl(doMes?.vencido_centavos)}
                 ajuda={n(doMes?.vencidos) > 0
                   ? `${inteiro(doMes?.vencidos)} para cobrar`
                   : 'nada atrasado'} />
          </LinhaDeKpis>

          <ColunasEmpilhadas
            titulo="Faturamento, mês a mês"
            subtitulo="Recebido é o que entrou; em aberto é o que foi lançado e ainda não."
            periodos={serie.map((s) => ({
              chave: s.mes, rotulo: mesCurto(s.mes),
              valores: {
                recebido: n(s.resumo.recebido_centavos),
                aberto: n(s.resumo.aberto_centavos),
              },
            }))}
            segmentos={[
              { chave: 'recebido', rotulo: 'Recebido', cor: 'var(--cl-viz-serie-1)' },
              { chave: 'aberto', rotulo: 'Em aberto', cor: 'var(--cl-viz-serie-2)' },
            ]}
            formatar={brlCompacto}
          />

          <div className="rel__dupla">
            <BarrasRanking
              titulo="Quem atendeu"
              subtitulo="Atendimentos concluídos na competência."
              linhas={desemp.map((d) => {
                const meta = metas.find(
                  (m) => m.alvo_tipo === 'PROFISSIONAL' && m.alvo_id === d.profissional_id)
                return {
                  id: d.profissional_id,
                  nome: d.profissional,
                  valor: n(d.atendimentos),
                  cor: d.cor,
                  meta: meta ? n(meta.alvo_valor) : null,
                  detalhe: `${brl(d.bruto_centavos)} faturados`,
                }
              })}
              formatar={(v) => inteiro(v)}
              unidade="atendimentos"
              vazio={<p className="ajuda">Nenhum atendimento concluído neste mês.</p>}
            />

            <Funil
              titulo="Do horário marcado ao atendimento"
              subtitulo="Onde a agenda perde hora."
              etapas={[
                { chave: 'marcadas', rotulo: 'Marcados', n: funil.marcadas,
                  cor: 'var(--cl-viz-etapa-1)' },
                { chave: 'compareceu', rotulo: 'Compareceram',
                  n: funil.marcadas - funil.faltas - funil.cancel,
                  cor: 'var(--cl-viz-etapa-2)',
                  perdas: [
                    { rotulo: 'não vieram', n: funil.faltas, cor: 'var(--cl-erro)',
                      acao: 'confirmar por WhatsApp reduz falta' },
                    { rotulo: 'cancelaram', n: funil.cancel, cor: 'var(--cl-atencao)',
                      acao: 'a lista de espera reaproveita o horário' },
                  ] },
                { chave: 'concluidos', rotulo: 'Concluídos', n: funil.feitas,
                  cor: 'var(--cl-viz-etapa-3)' },
              ]}
            />
          </div>

          <BarrasRanking
            titulo="Quem marcou"
            subtitulo="Marcações feitas por cada pessoa da equipe, e quanto delas virou atendimento."
            linhas={prod.map((p) => ({
              id: p.usuario_id,
              nome: p.usuario,
              valor: n(p.marcacoes),
              detalhe: p.aproveitamento_pct != null
                ? `${pct(p.aproveitamento_pct)} viraram atendimento`
                : `${inteiro(p.concretizadas)} concluídos`,
            }))}
            formatar={(v) => inteiro(v)}
            unidade="marcações"
            vazio={<p className="ajuda">Ninguém marcou nada neste mês.</p>}
          />

          {/* Dito na tela, não escondido: a clínica descobriria sozinha
              e no pior momento. */}
          <p className="rel__ressalva ajuda">
            Estes números contam o que <strong>entra</strong>. Despesa não existe no
            Clinuma ainda, então "recebido" nunca vira "sobrou" — e comissão por
            profissional e por duração ainda não é configurável aqui.
          </p>
        </>
      )}
    </div>
  )
}
